import React, { useEffect, useMemo, useRef, useState } from 'react';
import { auth } from './firebase';
import './CatalogoTabla.css';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { listarListas, agregarAutoALista as agregarAutoAListaApi } from './api/listas';
import { listarCatalogo, obtenerMisClasificaciones, guardarMiClasificacion } from './api/catalogo';
import { Search, Plus, FileDown, Calendar, ChevronLeft, ChevronRight, X, ArrowUpDown, ArrowUp, ArrowDown, Settings2, GripVertical, Eye, EyeOff, RefreshCw, ClipboardList, Gauge } from 'lucide-react';
import * as XLSX from 'xlsx';
import CarLoader from './components/CarLoader';
import ValueScoreRanking from './ValueScoreRanking';

/* ── sessionStorage helpers ────────────────────────────────── */
const SS_KEY = 'catalogo_filtros_v1';

const readSession = () => {
  try {
    const s = sessionStorage.getItem(SS_KEY);
    return s ? JSON.parse(s) : {};
  } catch { return {}; }
};

const writeSession = (partial) => {
  try {
    const current = readSession();
    sessionStorage.setItem(SS_KEY, JSON.stringify({ ...current, ...partial }));
  } catch { /* ignore */ }
};

/* ── MultiSelect ───────────────────────────────────────────── */
function MultiSelectDropdown({ options, selected, onChange, placeholder = "Filtrar…" }) {
  const [open, setOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);
  const filtered = (options || []).filter(o => String(o ?? '').toLowerCase().includes(queryText.toLowerCase()));
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  const label = selected.length === 0 ? placeholder : selected.length === 1 ? selected[0] : `${selected.length} selec.`;
  return (
    <div className="ms-container" ref={ref}>
      <button type="button" className="ms-trigger" onClick={() => setOpen(o => !o)}>
        {label}<span className={`ms-caret ${open ? 'open' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="ms-panel" role="listbox">
          <div className="ms-toolbar">
            <input className="ms-search" placeholder="Buscar…" value={queryText} onChange={e => setQueryText(e.target.value)} />
            <button className="ms-action" type="button" onClick={() => onChange([...filtered])}>Todos</button>
            <button className="ms-action" type="button" onClick={() => onChange([])}>Limpiar</button>
          </div>
          <div className="ms-options">
            {filtered.length === 0 && <div className="ms-empty">Sin resultados</div>}
            {filtered.map(opt => (
              <label key={opt} className="ms-option">
                <input type="checkbox" className="ms-checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SortIcon ──────────────────────────────────────────────── */
function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <ArrowUpDown size={12} style={{ marginLeft: 4, opacity: 0.35 }} />;
  return sortDir === 'asc'
    ? <ArrowUp size={12} style={{ marginLeft: 4, color: '#2563eb' }} />
    : <ArrowDown size={12} style={{ marginLeft: 4, color: '#2563eb' }} />;
}

/* ── Column Configurator Panel ─────────────────────────────── */
function ColumnConfigurator({ columns, onClose, onApply }) {
  const [cols, setCols] = useState(() => columns.map(c => ({ ...c })));
  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);

  const toggle = (key) => {
    setCols(prev => prev.map(c => c.key === key && !c.fixed ? { ...c, visible: !c.visible } : c));
  };

  const onDragStart = (e, i) => { dragIdx.current = i; e.dataTransfer.effectAllowed = 'move'; };
  const onDragEnter = (i) => { dragOverIdx.current = i; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = () => {
    if (dragIdx.current === null || dragOverIdx.current === null || dragIdx.current === dragOverIdx.current) return;
    const next = [...cols];
    const [moved] = next.splice(dragIdx.current, 1);
    next.splice(dragOverIdx.current, 0, moved);
    setCols(next);
    dragIdx.current = null;
    dragOverIdx.current = null;
  };

  const visibleCount = cols.filter(c => c.visible).length;
  const resetToDefault = () => setCols(columns.map(c => ({ ...c })));

  return (
    <div className="col-cfg-overlay" onClick={onClose}>
      <div className="col-cfg-panel" onClick={e => e.stopPropagation()}>
        <div className="col-cfg-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Settings2 size={16} style={{ color: '#2563eb' }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>Configurar columnas</span>
          </div>
          <button className="col-cfg-close" onClick={onClose}><X size={14} /></button>
        </div>
        <p style={{ fontSize: 12, color: '#9ca3af', padding: '8px 16px 4px' }}>
          Arrastra ⠿ para reordenar · Ojo para mostrar/ocultar · <strong>Marca</strong> es fija
        </p>
        <div className="col-cfg-list">
          {cols.map((col, i) => (
            <div
              key={col.key}
              className={`col-cfg-item${!col.visible ? ' col-cfg-item--hidden' : ''}${col.fixed ? ' col-cfg-item--fixed' : ''}`}
              draggable={!col.fixed}
              onDragStart={e => !col.fixed && onDragStart(e, i)}
              onDragEnter={() => !col.fixed && onDragEnter(i)}
              onDragOver={onDragOver}
              onDrop={onDrop}
            >
              <span className="col-cfg-grip" style={{ opacity: col.fixed ? 0.15 : 0.5 }}>
                <GripVertical size={14} />
              </span>
              <span className="col-cfg-label" style={{ opacity: col.visible ? 1 : 0.45 }}>
                {col.labelFull || col.label}
              </span>
              {col.fixed ? (
                <span style={{ fontSize: 10, color: '#d1d5db', marginLeft: 'auto', fontStyle: 'italic' }}>fija</span>
              ) : (
                <button
                  className={`col-cfg-eye${col.visible ? ' col-cfg-eye--on' : ' col-cfg-eye--off'}`}
                  onClick={() => toggle(col.key)}
                  title={col.visible ? 'Ocultar columna' : 'Mostrar columna'}
                >
                  {col.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="col-cfg-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{visibleCount} visibles de {cols.length}</span>
            <button className="ct-btn" style={{ fontSize: 11 }} onClick={resetToDefault}>Restablecer</button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="ct-btn" onClick={onClose}>Cancelar</button>
            <button className="ct-btn ct-btn--primary" onClick={() => onApply(cols)}>Aplicar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Badge de tipo motor ───────────────────────────────────── */
const TIPOMOTOR_COLORS = {
  'Eléctrico': { bg: '#ecfdf5', color: '#065f46', dot: '#10b981' },
  'Híbrido enchufable': { bg: '#f0fdf4', color: '#166534', dot: '#22c55e' },
  'Híbrido': { bg: '#f0fdf4', color: '#166534', dot: '#4ade80' },
  'Diésel': { bg: '#fffbeb', color: '#78350f', dot: '#f59e0b' },
  'Bencina': { bg: '#eff6ff', color: '#1e40af', dot: '#3b82f6' },
  'GNC': { bg: '#fdf4ff', color: '#6b21a8', dot: '#a855f7' },
};

function TipomotorBadge({ value }) {
  if (!value || value === '—') return <span style={{ color: '#d1d5db' }}>—</span>;
  const style = TIPOMOTOR_COLORS[value] || { bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: style.bg, color: style.color,
      borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: style.dot, flexShrink: 0 }} />
      {value}
    </span>
  );
}

/* ── Columnas base ─────────────────────────────────────────── */
const BASE_COLS = [
  { key: 'marca', label: 'Marca', labelFull: 'Marca', visible: true, fixed: true, isPrice: false },
  { key: 'model', label: 'Modelo', labelFull: 'Modelo', visible: true, fixed: false, isPrice: false },
  { key: 'modelDetail', label: 'Versión', labelFull: 'Versión', visible: true, fixed: false, isPrice: false },
  { key: 'categoria', label: 'Categoría', labelFull: 'Categoría', visible: true, fixed: false, isPrice: false },
  { key: 'tipomotor', label: 'Tipo motor', labelFull: 'Tipo de motor', visible: true, fixed: false, isPrice: false },
  { key: 'date_add', label: 'Fecha', labelFull: 'Fecha captura', visible: true, fixed: false, isPrice: false },
  { key: 'origen', label: 'Origen', labelFull: 'Origen', visible: false, fixed: false, isPrice: false },
  { key: 'miClasificacion', label: 'Mi clasif.', labelFull: 'Mi clasificación (privada — solo tú la ves)', visible: false, fixed: false, isPrice: false },
];

/* ── Opciones de filtro tipo motor ─────────────────────────── */
const TIPOMOTOR_OPTIONS = ['Bencina', 'Diésel', 'Híbrido', 'Híbrido enchufable', 'Eléctrico', 'GNC'];

/* ── CatalogoTabla ─────────────────────────────────────────── */
/* La deduplicación y la herencia de campos curados (categoria/
   tipomotor/origen) ya no viven acá — listarCatalogo() las resuelve
   server-side contra Postgres: vehicle_versions guarda esos campos
   en la identidad del vehículo, nunca en el snapshot semanal, así
   que un snapshot nuevo no puede volver a dejarlos vacíos. */

function CatalogoTabla() {
  const [vista, setVista] = useState('catalogo'); // 'catalogo' | 'valueScore'
  const [listasUsuario, setListasUsuario] = useState([]);
  const [listasUsuarioLoading, setListasUsuarioLoading] = useState(true);
  const [autoParaAgregar, setAutoParaAgregar] = useState(null);
  const [modalListasVisible, setModalListasVisible] = useState(false);
  const [autos, setAutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [ultimaFechaStr, setUltimaFechaStr] = useState('');

  /* ── Mi clasificación: subclasificación privada por usuario, no confundir
     con categoria/tipomotor/origen (esos son curados globales). ── */
  const [misClasificaciones, setMisClasificaciones] = useState({}); // { vehicleVersionId: texto }
  const [editandoClasifId, setEditandoClasifId] = useState(null);
  const [clasifInput, setClasifInput] = useState('');
  useEffect(() => { obtenerMisClasificaciones().then(setMisClasificaciones).catch(console.error); }, []);
  const sugerenciasClasificacion = useMemo(
    () => [...new Set(Object.values(misClasificaciones))].filter(Boolean).sort(),
    [misClasificaciones]
  );
  const iniciarEdicionClasif = (auto) => { setEditandoClasifId(auto.id); setClasifInput(misClasificaciones[auto.id] || ''); };
  const guardarClasif = async (id) => {
    const texto = clasifInput.trim();
    setEditandoClasifId(null);
    try {
      await guardarMiClasificacion(id, texto);
      setMisClasificaciones(prev => {
        const next = { ...prev };
        if (texto) next[id] = texto; else delete next[id];
        return next;
      });
    } catch (e) { console.error(e); toast.error('No se pudo guardar tu clasificación'); }
  };

  /* ── Estado de búsqueda y filtros — se inicializa desde sessionStorage ── */
  const _ss = readSession();
  const [search, setSearchRaw] = useState(_ss.search ?? '');
  const [marcasFiltro, setMarcasFiltroRaw] = useState(_ss.marcasFiltro ?? []);
  const [modelosFiltro, setModelosFiltroRaw] = useState(_ss.modelosFiltro ?? []);
  const [categoriasFiltro, setCategoriasFiltroRaw] = useState(_ss.categoriasFiltro ?? []);
  const [tipomotorFiltro, setTipomotorFiltroRaw] = useState(_ss.tipomotorFiltro ?? []);
  const [clasificacionFiltro, setClasificacionFiltroRaw] = useState(_ss.clasificacionFiltro ?? []);
  const [sortField, setSortField] = useState(_ss.sortField ?? 'marca');
  const [sortDir, setSortDir] = useState(_ss.sortDir ?? 'asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_ss.pageSize ?? 25);

  /* ── Wrappers que escriben en sessionStorage al mismo tiempo ── */
  const setSearch = (v) => { setSearchRaw(v); writeSession({ search: v }); };
  const setMarcasFiltro = (v) => { setMarcasFiltroRaw(v); writeSession({ marcasFiltro: v }); };
  const setModelosFiltro = (v) => { setModelosFiltroRaw(v); writeSession({ modelosFiltro: v }); };
  const setCategoriasFiltro = (v) => { setCategoriasFiltroRaw(v); writeSession({ categoriasFiltro: v }); };
  const setTipomotorFiltro = (v) => { setTipomotorFiltroRaw(v); writeSession({ tipomotorFiltro: v }); };
  const setClasificacionFiltro = (v) => { setClasificacionFiltroRaw(v); writeSession({ clasificacionFiltro: v }); };

  const [showColCfg, setShowColCfg] = useState(false);
  const [savedColConfig, setSavedColConfig] = useState(() => {
    try {
      const s = localStorage.getItem('catalogo_col_config_v2');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });

  /* ── Wrappers sort que también persisten ─────────────────── */
  const setSort = (f) => {
    if (sortField === f) {
      const next = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(next);
      writeSession({ sortDir: next });
    } else {
      setSortField(f);
      setSortDir('asc');
      writeSession({ sortField: f, sortDir: 'asc' });
    }
  };

  const setPageSizePersist = (v) => { setPageSize(v); writeSession({ pageSize: v }); };

  /* ── Indicador visual de filtros activos ─────────────────── */
  const hayFiltrosActivos = search.trim() || marcasFiltro.length || modelosFiltro.length ||
    categoriasFiltro.length || tipomotorFiltro.length || clasificacionFiltro.length;

  const limpiarFiltros = () => {
    setSearch('');
    setMarcasFiltro([]);
    setModelosFiltro([]);
    setCategoriasFiltro([]);
    setTipomotorFiltro([]);
    setClasificacionFiltro([]);
    setPage(1);
  };

  /* ── Guard de concurrencia: descarta respuestas de cargas obsoletas ── */
  const loadIdRef = useRef(0);

  const cargarAutosPorRango = async (desdeStr, hastaStr) => {
    if (!desdeStr || !hastaStr) return;
    if (hastaStr < desdeStr) { toast.error('La fecha "Hasta" no puede ser anterior a "Desde".'); return; }
    const myLoadId = ++loadIdRef.current;
    setLoading(true);
    try {
      const { autos: autosNuevos } = await listarCatalogo({ desde: desdeStr, hasta: hastaStr });
      if (myLoadId !== loadIdRef.current) return; // se disparó una carga más nueva mientras esperábamos
      setAutos(autosNuevos);
      setFechaDesde(desdeStr);
      setFechaHasta(hastaStr);
      setPage(1);
    } catch (e) {
      if (myLoadId !== loadIdRef.current) return;
      console.error(e); setAutos([]);
      toast.error('No se pudieron cargar los modelos para ese rango');
    } finally {
      if (myLoadId === loadIdRef.current) setLoading(false);
    }
  };

  const cargarTodoSinFiltro = async () => {
    const myLoadId = ++loadIdRef.current;
    setLoading(true);
    try {
      const { autos: autosNuevos } = await listarCatalogo({ todos: true });
      if (myLoadId !== loadIdRef.current) return;
      toast.info(`${autosNuevos.length} modelos únicos`);
      // Ya no hay un rango de fechas aplicado — limpiar para no mostrar un rango engañoso en el header
      setFechaDesde('');
      setFechaHasta('');
      setAutos(autosNuevos); setPage(1);
    } catch (e) {
      if (myLoadId !== loadIdRef.current) return;
      console.error(e); toast.error('Error al cargar todos los modelos');
    } finally {
      if (myLoadId === loadIdRef.current) setLoading(false);
    }
  };

  const cargarUltimaCarga = async () => {
    const myLoadId = ++loadIdRef.current;
    setLoading(true);
    try {
      const { autos: autosNuevos, ultimaFecha } = await listarCatalogo();
      if (myLoadId !== loadIdRef.current) return;
      setUltimaFechaStr(ultimaFecha || '');
      setFechaDesde(ultimaFecha || '');
      setFechaHasta(ultimaFecha || '');
      setAutos(autosNuevos);
      setPage(1);
    } catch (e) {
      if (myLoadId !== loadIdRef.current) return;
      console.error(e); setAutos([]);
      toast.error('No se pudo cargar el catálogo');
    } finally {
      if (myLoadId === loadIdRef.current) setLoading(false);
    }
  };

  useEffect(() => { cargarUltimaCarga(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setListasUsuarioLoading(false); return; }
    listarListas().then(setListasUsuario).finally(() => setListasUsuarioLoading(false));
  }, []);

  const agregarAutoALista = async (listaId, auto) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await agregarAutoAListaApi(listaId, auto.marca, auto.model, auto.modelDetail);
      toast.success('Auto agregado al proyecto');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo agregar el auto al proyecto');
    }
    setModalListasVisible(false); setAutoParaAgregar(null);
  };

  const marcasDisponibles = useMemo(() =>
    Array.from(new Set(autos.map(a => a.marca).filter(Boolean))).sort(), [autos]);
  const modelosDisponibles = useMemo(() => {
    const f = marcasFiltro.length ? autos.filter(a => marcasFiltro.includes(a.marca)) : autos;
    return Array.from(new Set(f.map(a => a.model).filter(Boolean))).sort();
  }, [autos, marcasFiltro]);
  const categoriasDisponibles = useMemo(() =>
    Array.from(new Set(autos.map(a => a.categoria).filter(Boolean))).sort(), [autos]);

  const tipomotorConteos = useMemo(() => {
    const counts = {};
    for (const a of autos) {
      if (a.tipomotor) counts[a.tipomotor] = (counts[a.tipomotor] || 0) + 1;
    }
    return counts;
  }, [autos]);

  const PRICE_TYPES = useMemo(() => {
    const freq = new Map();
    for (const a of autos)
      for (const t of (Array.isArray(a?.tiposprecio) ? a.tiposprecio : [])) {
        const k = String(t ?? '').trim();
        if (k) freq.set(k, (freq.get(k) || 0) + 1);
      }
    const ordered = Array.from(freq.entries()).sort((a, b) => b[1] - a[1]).map(([k]) => k);
    while (ordered.length < 4) ordered.push(`Precio ${ordered.length + 1}`);
    return ordered.slice(0, 4);
  }, [autos]);

  const allColConfig = useMemo(() => {
    const priceCols = PRICE_TYPES.map(t => ({
      key: `price:${t}`, label: t.length > 16 ? t.slice(0, 15) + '…' : t,
      labelFull: t, visible: true, fixed: false, isPrice: true,
    }));
    const defaultCols = [...BASE_COLS, ...priceCols];
    if (!savedColConfig) return defaultCols;
    const savedKeys = savedColConfig.map(c => c.key);
    const result = savedColConfig
      .map(saved => {
        const def = defaultCols.find(d => d.key === saved.key);
        return def ? { ...def, visible: saved.visible } : null;
      }).filter(Boolean);
    defaultCols.forEach(def => { if (!savedKeys.includes(def.key)) result.push(def); });
    return result;
  }, [PRICE_TYPES, savedColConfig]);

  const visibleCols = useMemo(() => allColConfig.filter(c => c.visible), [allColConfig]);

  const handleApplyColConfig = (newCols) => {
    const toSave = newCols.map(c => ({ key: c.key, visible: c.visible }));
    setSavedColConfig(newCols);
    try { localStorage.setItem('catalogo_col_config_v2', JSON.stringify(toSave)); } catch { }
    setShowColCfg(false);
    toast.success('Columnas actualizadas');
  };

  const getPriceByType = (auto, typeName) => {
    const tipos = Array.isArray(auto?.tiposprecio) ? auto.tiposprecio : [];
    const precios = Array.isArray(auto?.precio) ? auto.precio : (auto?.precio != null ? [auto.precio] : []);
    const idx = tipos.findIndex(t => String(t).toLowerCase() === String(typeName).toLowerCase());
    return idx >= 0 ? precios[idx] : undefined;
  };
  const parsePrecioByType = (auto, typeName) => {
    const v = getPriceByType(auto, typeName);
    const n = Number(String(v ?? '').replace(/[^0-9]/g, ''));
    return Number.isNaN(n) ? NaN : n;
  };
  const formatCLPValue = (v) => {
    const n = Number(String(v ?? '').replace(/[^0-9]/g, ''));
    if (!v || Number.isNaN(n) || n === 0) return null;
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);
  };

  const getCellValue = (a, col) => {
    if (col.isPrice) return formatCLPValue(getPriceByType(a, col.key.slice(6)));
    if (col.key === 'date_add') return a.date_add ? new Date(Number(a.date_add) * 1000).toLocaleDateString('es-CL') : null;
    return a[col.key] || null;
  };

  const { total, pageItems, allItems } = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = autos
      .map(a => misClasificaciones[a.id] ? { ...a, miClasificacion: misClasificaciones[a.id] } : a)
      .filter(a => {
        const okMarca = !marcasFiltro.length || marcasFiltro.includes(a.marca);
        const okModelo = !modelosFiltro.length || modelosFiltro.includes(a.model);
        const okCat = !categoriasFiltro.length || categoriasFiltro.includes(a.categoria);
        const okTipo = !tipomotorFiltro.length || tipomotorFiltro.includes(a.tipomotor);
        const okClasif = !clasificacionFiltro.length || clasificacionFiltro.includes(a.miClasificacion);
        const okSearch = !term || `${a.marca} ${a.model} ${a.modelDetail} ${a.categoria} ${a.tipomotor || ''} ${a.miClasificacion || ''}`.toLowerCase().includes(term);
        return okMarca && okModelo && okCat && okTipo && okClasif && okSearch;
      });
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortField.startsWith('price:')) {
        const t = sortField.slice(6);
        const va = parsePrecioByType(a, t), vb = parsePrecioByType(b, t);
        if (isNaN(va) && isNaN(vb)) return 0;
        if (isNaN(va)) return 1; if (isNaN(vb)) return -1;
        return (va > vb ? 1 : va < vb ? -1 : 0) * dir;
      }
      if (sortField === 'date_add') return (Number(a.date_add || 0) - Number(b.date_add || 0)) * dir;
      const sa = String(a[sortField] ?? '').toLowerCase();
      const sb = String(b[sortField] ?? '').toLowerCase();
      return (sa > sb ? 1 : sa < sb ? -1 : 0) * dir;
    });
    const start = (page - 1) * pageSize;
    return { total: list.length, pageItems: list.slice(start, start + pageSize), allItems: list };
  }, [autos, misClasificaciones, search, marcasFiltro, modelosFiltro, categoriasFiltro, tipomotorFiltro, clasificacionFiltro, sortField, sortDir, page, pageSize]);

  useEffect(() => setPage(1), [search, marcasFiltro, modelosFiltro, categoriasFiltro, tipomotorFiltro, clasificacionFiltro, pageSize]);

  /* ── Reset del filtro "Modelo" cuando cambia la Marca ─────────
     Se omite en el montaje inicial para no borrar un filtro restaurado
     desde sessionStorage, y usa el setter que persiste el cambio. */
  const isFirstMarcaRender = useRef(true);
  useEffect(() => {
    if (isFirstMarcaRender.current) { isFirstMarcaRender.current = false; return; }
    setModelosFiltro([]);
  }, [marcasFiltro]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1, canNext = page < totalPages;

  const fmtFecha = (date_add) => !date_add ? '' : new Date(Number(date_add) * 1000).toLocaleDateString('es-CL');
  const mesKey = (date_add) => {
    if (!date_add) return '';
    const d = new Date(Number(date_add) * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const buildRows = (source) => source.map(r => {
    const base = { Marca: r.marca ?? '', Modelo: r.model ?? '', Version: r.modelDetail ?? '', 'Tipo motor': r.tipomotor ?? '', Categoría: r.categoria ?? '', 'Mi clasificación': misClasificaciones[r.id] ?? '', Fecha: fmtFecha(r.date_add) };
    PRICE_TYPES.forEach(t => {
      const n = parsePrecioByType(r, t);
      base[`Precio (${t})`] = Number.isFinite(n) && n > 0 ? n : '';
    });
    return base;
  });
  const exportToExcel = (all = false) => {
    const data = buildRows(all ? allItems : pageItems);
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Catálogo');
    XLSX.writeFile(wb, `catalogo_${all ? 'completo' : 'pagina'}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const exportMensual = () => {
    const grupoMap = new Map();
    for (const r of allItems) {
      if (!r.date_add) continue;
      const clave = `${r.marca}|${r.model}|${r.modelDetail}|${mesKey(r.date_add)}`;
      const ex = grupoMap.get(clave);
      if (!ex || Number(r.date_add) > Number(ex.date_add)) grupoMap.set(clave, r);
    }
    const rows = Array.from(grupoMap.values()).sort((a, b) =>
      `${a.marca}|${a.model}|${a.modelDetail}|${mesKey(a.date_add)}`.localeCompare(
        `${b.marca}|${b.model}|${b.modelDetail}|${mesKey(b.date_add)}`
      )
    );
    if (!rows.length) { toast.info('No hay datos para generar el reporte mensual.'); return; }
    const data = rows.map(r => ({
      Mes: mesKey(r.date_add), Marca: r.marca ?? '', Modelo: r.model ?? '',
      Version: r.modelDetail ?? '', 'Tipo motor': r.tipomotor ?? '', Categoría: r.categoria ?? '', Fecha: fmtFecha(r.date_add),
      ...Object.fromEntries(PRICE_TYPES.map(t => {
        const n = parsePrecioByType(r, t);
        return [`Precio (${t})`, Number.isFinite(n) && n > 0 ? n : ''];
      })),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte Mensual');
    XLSX.writeFile(wb, `reporte_mensual_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Reporte mensual: ${rows.length} registros`);
  };

  const rangoDisplay = fechaDesde && fechaHasta
    ? `${fechaDesde.split('-').reverse().join('/')} — ${fechaHasta.split('-').reverse().join('/')}`
    : '';

  const sinTipomotor = autos.filter(a => !a.tipomotor).length;

  return (
    <div className="catalogo-main">

      {/* Header */}
      <div className="catalogo-header">
        <div className="catalogo-header-left">
          <img src="/logo_doctorcar.png" alt="Logo" className="catalogo-header-logo" />
          <div>
            <h2 className="catalogo-header-title">Catálogo de modelos</h2>
            {!loading && (
              <p className="catalogo-header-meta">
                {ultimaFechaStr && fechaDesde === ultimaFechaStr && fechaHasta === ultimaFechaStr
                  ? <>📅 Última carga · hasta {ultimaFechaStr.split('-').reverse().join('/')} (ventana de 7 días) · <strong>{total}</strong> modelos</>
                  : rangoDisplay
                    ? <>{rangoDisplay} · <strong>{total}</strong> modelos</>
                    : <>📋 Todos los modelos cargados (sin filtro de fecha) · <strong>{total}</strong> modelos</>
                }
              </p>
            )}
          </div>
        </div>
        <div className="catalogo-header-actions">
          <Link to="/informe-carga" className="ct-btn" title="Comparar la última carga contra la anterior para ver qué faltó">
            <ClipboardList size={14} /> Informe última carga
          </Link>
          <button className="ct-btn" onClick={() => setShowColCfg(true)} title="Configurar columnas visibles y su orden">
            <Settings2 size={14} /> Columnas
          </button>
          <button className="ct-btn" onClick={() => exportToExcel(false)}><FileDown size={14} /> Exportar página</button>
          <button className="ct-btn" onClick={() => exportToExcel(true)}><FileDown size={14} /> Exportar todo</button>
          <button className="ct-btn ct-btn--primary" onClick={exportMensual}><FileDown size={14} /> Reporte mensual</button>
        </div>
      </div>

      {/* Vista */}
      <div className="catalogo-view-tabs">
        <button className={`catalogo-view-tab ${vista === 'catalogo' ? 'active' : ''}`} onClick={() => setVista('catalogo')}>
          Catálogo
        </button>
        <button className={`catalogo-view-tab ${vista === 'valueScore' ? 'active' : ''}`} onClick={() => setVista('valueScore')}>
          <Gauge size={13} /> Value Score
        </button>
      </div>

      {vista === 'valueScore' ? (
        <ValueScoreRanking />
      ) : (
      <>
      {/* Toolbar */}
      <div className="catalogo-toolbar">
        <div className="catalogo-date-group">
          <Calendar size={14} style={{ color: '#9ca3af' }} />
          <label>Desde</label>
          <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
          <span className="catalogo-date-sep">→</span>
          <label>Hasta</label>
          <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
        </div>
        <button className="ct-btn ct-btn--primary" onClick={() => cargarAutosPorRango(fechaDesde, fechaHasta)}>Aplicar</button>
        {ultimaFechaStr && (fechaDesde !== ultimaFechaStr || fechaHasta !== ultimaFechaStr) && (
          <button className="ct-btn"
            title={`Volver a la última carga (${ultimaFechaStr.split('-').reverse().join('/')})`}
            onClick={cargarUltimaCarga}>
            ↩ Última carga
          </button>
        )}
        <button className="ct-btn" onClick={cargarTodoSinFiltro}>Ver todos</button>
        <button className="ct-btn ct-btn--primary" title="Recarga los datos desde el servidor"
          onClick={() => {
            if (ultimaFechaStr && fechaDesde === ultimaFechaStr && fechaHasta === ultimaFechaStr) cargarUltimaCarga();
            else if (fechaDesde && fechaHasta) cargarAutosPorRango(fechaDesde, fechaHasta);
            else cargarTodoSinFiltro();
          }}
          disabled={loading}>
          <RefreshCw size={14} /> Actualizar datos
        </button>
        <div className="catalogo-toolbar-divider" />
        <div className="catalogo-search">
          <span className="catalogo-search-icon"><Search size={14} /></span>
          <input
            type="text"
            placeholder="Buscar marca, modelo, tipo motor…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {/* X para limpiar solo el campo de texto */}
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                color: '#9ca3af', display: 'flex', alignItems: 'center',
              }}
              title="Limpiar búsqueda"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <MultiSelectDropdown options={marcasDisponibles} selected={marcasFiltro} onChange={setMarcasFiltro} placeholder="Marca" />
        <MultiSelectDropdown options={modelosDisponibles} selected={modelosFiltro} onChange={setModelosFiltro} placeholder="Modelo" />
        <MultiSelectDropdown options={categoriasDisponibles} selected={categoriasFiltro} onChange={setCategoriasFiltro} placeholder="Categoría" />
        <MultiSelectDropdown options={TIPOMOTOR_OPTIONS} selected={tipomotorFiltro} onChange={setTipomotorFiltro} placeholder="Tipo motor" />
        {sugerenciasClasificacion.length > 0 && (
          <MultiSelectDropdown options={sugerenciasClasificacion} selected={clasificacionFiltro} onChange={setClasificacionFiltro} placeholder="Mi clasificación" />
        )}

        {/* Botón limpiar todos los filtros — solo visible si hay alguno activo */}
        {hayFiltrosActivos && (
          <button
            className="ct-btn"
            onClick={limpiarFiltros}
            title="Limpiar todos los filtros"
            style={{ color: '#dc2626', borderColor: '#fca5a5' }}
          >
            <X size={13} /> Limpiar filtros
          </button>
        )}
      </div>

      {/* Pills de filtro rápido por tipo motor */}
      {autos.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 0 2px' }}>
          <button
            onClick={() => setTipomotorFiltro([])}
            style={{
              padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', border: '1.5px solid', fontFamily: 'inherit',
              background: tipomotorFiltro.length === 0 ? '#1e293b' : 'transparent',
              color: tipomotorFiltro.length === 0 ? '#fff' : '#64748b',
              borderColor: tipomotorFiltro.length === 0 ? '#1e293b' : '#e2e8f0',
            }}>
            Todos ({autos.length})
          </button>
          {TIPOMOTOR_OPTIONS.map(tipo => {
            const count = tipomotorConteos[tipo] || 0;
            if (count === 0) return null;
            const style = TIPOMOTOR_COLORS[tipo] || { bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' };
            const active = tipomotorFiltro.length === 1 && tipomotorFiltro[0] === tipo;
            return (
              <button key={tipo}
                onClick={() => setTipomotorFiltro(active ? [] : [tipo])}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', border: '1.5px solid', fontFamily: 'inherit',
                  background: active ? style.bg : 'transparent',
                  color: active ? style.color : '#64748b',
                  borderColor: active ? style.dot : '#e2e8f0',
                }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: style.dot }} />
                {tipo} ({count})
              </button>
            );
          })}
          {sinTipomotor > 0 && (
            <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center', marginLeft: 4 }}>
              · {sinTipomotor} sin clasificar
            </span>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="catalogo-summary">
        <span className="catalogo-summary-text">
          {loading ? 'Cargando…' : (
            <>
              <strong>{total}</strong> modelos
              {hayFiltrosActivos && <span style={{ color: '#f59e0b', marginLeft: 6, fontSize: 11 }}>● filtros activos</span>}
              {' · '}
              <span style={{ color: '#9ca3af', fontSize: 12 }}>{visibleCols.length} columnas visibles</span>
            </>
          )}
        </span>
        <div className="catalogo-summary-right">
          <select className="catalogo-page-select" value={pageSize} onChange={e => setPageSizePersist(Number(e.target.value))}>
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / página</option>)}
          </select>
          <span className="catalogo-page-info">Página <strong>{page}</strong> de {totalPages}</span>
          <button className="ct-btn ct-btn--ghost" disabled={!canPrev} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></button>
          <button className="ct-btn ct-btn--ghost" disabled={!canNext} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></button>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="catalogo-state"><CarLoader label="Cargando modelos…" /></div>
      ) : total === 0 ? (
        <div className="catalogo-state">
          <div className="catalogo-state-icon">🔍</div>
          <p>No se encontraron resultados</p>
          {hayFiltrosActivos && (
            <button className="ct-btn" onClick={limpiarFiltros} style={{ marginTop: 8 }}>
              <X size={13} /> Limpiar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="catalogo-table-wrap">
          <datalist id="ct-clasificaciones-sugeridas">
            {sugerenciasClasificacion.map(s => <option key={s} value={s} />)}
          </datalist>
          <table className="catalogo-table">
            <thead>
              <tr>
                {visibleCols.map(col => (
                  <th key={col.key}
                    className={`sortable${col.isPrice ? ' price-col' : ''}${sortField === col.key ? ' sorted' : ''}`}
                    onClick={() => setSort(col.key)}
                    title={col.labelFull || col.label}>
                    {col.label}
                    <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                  </th>
                ))}
                <th style={{ width: 72, textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(a => (
                <tr key={a.id}>
                  {visibleCols.map(col => {
                    const val = getCellValue(a, col);
                    if (col.key === 'marca') return (
                      <td key={col.key}><span className="catalogo-marca-badge">{a.marca || '—'}</span></td>
                    );
                    if (col.key === 'model') return (
                      <td key={col.key} style={{ fontWeight: 500, color: '#111827' }}>{a.model || '—'}</td>
                    );
                    if (col.key === 'modelDetail') return (
                      <td key={col.key} style={{ color: '#6b7280' }}>{a.modelDetail || '—'}</td>
                    );
                    if (col.key === 'categoria') return (
                      <td key={col.key}>
                        {a.categoria ? <span className="catalogo-cat-chip">{a.categoria}</span> : <span style={{ color: '#d1d5db' }}>—</span>}
                      </td>
                    );
                    if (col.key === 'tipomotor') return (
                      <td key={col.key}><TipomotorBadge value={a.tipomotor} /></td>
                    );
                    if (col.key === 'miClasificacion') return (
                      <td key={col.key}>
                        {editandoClasifId === a.id ? (
                          <input
                            list="ct-clasificaciones-sugeridas"
                            autoFocus
                            value={clasifInput}
                            onChange={e => setClasifInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') guardarClasif(a.id); if (e.key === 'Escape') setEditandoClasifId(null); }}
                            onBlur={() => guardarClasif(a.id)}
                            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #c4b5fd', borderRadius: 5, width: 120 }}
                          />
                        ) : (
                          <span
                            onClick={() => iniciarEdicionClasif(a)}
                            title="Privada — solo tú la ves. Click para editar."
                            style={{ cursor: 'pointer', fontSize: 12, color: a.miClasificacion ? '#6d28d9' : '#d1d5db' }}>
                            {a.miClasificacion || '+ agregar'}
                          </span>
                        )}
                      </td>
                    );
                    if (col.isPrice) return (
                      <td key={col.key} className="price-col">
                        {val ? <span className="catalogo-price">{val}</span> : <span className="catalogo-price-empty">—</span>}
                      </td>
                    );
                    return (
                      <td key={col.key} style={{ color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {val || '—'}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
                      <Link to={`/modelo/${a.id}`} className="catalogo-action-btn" title="Ver detalle"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                        <Search size={15} strokeWidth={2} />
                      </Link>
                      <button className="catalogo-action-btn" title="Agregar a lista"
                        onClick={() => { setAutoParaAgregar(a); setModalListasVisible(true); }} style={{ lineHeight: 1 }}>
                        <Plus size={15} strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      {!loading && total > 0 && (
        <div className="catalogo-footer">
          <span className="catalogo-footer-text">
            Mostrando <strong>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}</strong> de <strong>{total}</strong>
          </span>
          <div className="catalogo-pagination">
            <button className="ct-btn" disabled={!canPrev} onClick={() => setPage(1)}>«</button>
            <button className="ct-btn" disabled={!canPrev} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /> Anterior</button>
            <span style={{ padding: '0 8px', fontSize: 13, color: '#6b7280' }}>{page} / {totalPages}</span>
            <button className="ct-btn" disabled={!canNext} onClick={() => setPage(p => p + 1)}>Siguiente <ChevronRight size={14} /></button>
            <button className="ct-btn" disabled={!canNext} onClick={() => setPage(totalPages)}>»</button>
          </div>
        </div>
      )}
      </>
      )}

      {/* Modal agregar a lista */}
      {modalListasVisible && autoParaAgregar && (
        <div className="catalogo-modal-overlay" onClick={() => setModalListasVisible(false)}>
          <div className="catalogo-modal" onClick={e => e.stopPropagation()}>
            <div className="catalogo-modal-header">
              <div>
                <p className="catalogo-modal-title">Agregar a proyecto</p>
                <p className="catalogo-modal-subtitle">{autoParaAgregar.marca} {autoParaAgregar.model} {autoParaAgregar.modelDetail}</p>
              </div>
              <button className="catalogo-modal-close" onClick={() => setModalListasVisible(false)}><X size={16} /></button>
            </div>
            {listasUsuarioLoading ? (
              <CarLoader label="Cargando proyectos…" size={18} />
            ) : listasUsuario.length === 0 ? (
              <p style={{ fontSize: 13, color: '#6b7280' }}>No tienes proyectos creados.</p>
            ) : (
              <div>
                {listasUsuario.map(lista => (
                  <button key={lista.id} className="catalogo-lista-btn" onClick={() => agregarAutoALista(lista.id, autoParaAgregar)}>
                    <Plus size={14} style={{ color: '#2563eb', flexShrink: 0 }} />
                    {(lista.nombre && String(lista.nombre).trim()) || lista.id}
                  </button>
                ))}
              </div>
            )}
            <button className="ct-btn" style={{ marginTop: 8, width: '100%' }} onClick={() => setModalListasVisible(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Configurador de columnas */}
      {showColCfg && (
        <ColumnConfigurator
          columns={allColConfig}
          onClose={() => setShowColCfg(false)}
          onApply={handleApplyColConfig}
        />
      )}
    </div>
  );
}

export default CatalogoTabla;